/**
 * Settings > About (2026-10-05): which build this device runs, and on demand
 * the device's frame numbers. Moved out of Navigate's footer, as
 * app-study.html has it ("The build number moves to Settings > About").
 *
 * The behaviour is Navigate's footer's, unchanged: the build SHA answers "is
 * this device on the deploy I just made"; Details (remembered per device)
 * shows the screen and frame readout and SENDS the device's own frame report
 * (`layout/frameReport.ts`) to every connected OPBridge, so a "cut off on the
 * iPad" report is read off the device's own numbers. The two lines that only
 * exist when something is wrong (a letterbox, the WebKit #254868 height
 * mismatch) show without Details.
 */

import { Show, createSignal, createMemo, onMount, type JSX } from 'solid-js';
import { bridges } from '../opbridge/store.ts';
import { measureViewportBox } from '../layout/trueViewport.ts';
import { measureFrame, sendFrameReport, type FrameReport } from '../layout/frameReport.ts';

const DETAILS_KEY = 'opbuttons.navigate.details.v1';
function readFlag(): boolean {
  try { return localStorage.getItem(DETAILS_KEY) === '1'; } catch { return false; }
}
function writeFlag(on: boolean): void {
  try { if (on) localStorage.setItem(DETAILS_KEY, '1'); else localStorage.removeItem(DETAILS_KEY); } catch { /* convenience only */ }
}

interface FrameInfo {
  availW: number; availH: number; vw: number; vh: number; dpr: number;
  designW: number; designH: number; scalePct: number;
  bandTop: number; bandBottom: number; bandSide: number;
  layoutH: number; unitH: number; standalone: boolean;
}

export function AboutDetails(): JSX.Element {
  const build = { sha: __BUILD_SHA__, dirty: __BUILD_SHA__.endsWith('-dirty'), time: __BUILD_TIME__ };
  const scopeMixed = createMemo(() => new Set(bridges().map((b) => b.scope_bundle_version ?? -1)).size > 1);
  const [detailsOpen, setDetailsOpen] = createSignal(readFlag());
  const [frameInfo, setFrameInfo] = createSignal<FrameInfo | null>(null);
  const [frame, setFrame] = createSignal<FrameReport | null>(null);
  const [frameSent, setFrameSent] = createSignal('');

  // What this device ACTUALLY is, measured from the DOM, never from a signal:
  // a readout whose job is "what is really on screen" must not report what
  // the app BELIEVES.
  onMount(() => {
    requestAnimationFrame(() => {
      const shell = document.querySelector('.shell') as HTMLElement | null;
      if (!shell) return;
      const fit = shell.parentElement as HTMLElement | null;
      const r = shell.getBoundingClientRect();
      const cs = getComputedStyle(shell);
      const designW = parseFloat(cs.width) || r.width;
      const designH = parseFloat(cs.height) || r.height;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const box = measureViewportBox();
      setFrameInfo({
        availW: fit ? fit.clientWidth : vw,
        availH: fit ? fit.clientHeight : vh,
        vw, vh,
        layoutH: box ? box.layoutH : vh,
        unitH: box ? box.unitH : 0,
        standalone: box ? box.standalone : false,
        dpr: window.devicePixelRatio || 1,
        designW: Math.round(designW),
        designH: Math.round(designH),
        scalePct: Math.round((r.width / designW) * 100),
        bandTop: Math.round(r.top),
        bandBottom: Math.round(vh - (r.top + r.height)),
        bandSide: Math.round(r.left),
      });
    });
    if (detailsOpen()) reportFrame();
  });

  function reportFrame(): void {
    requestAnimationFrame(() => {
      const r = measureFrame();
      setFrame(r);
      if (!r) return;
      setFrameSent('sending...');
      void sendFrameReport(r).then(({ ok, of }) => setFrameSent(of === 0 ? 'no OPBridge connected' : `sent to ${ok} of ${of} OPBridge`));
    });
  }

  return (
    <div class="about-details" data-open={detailsOpen() ? 'true' : 'false'}>
      <div class="nav-well">
      <div class="nav-lr">
        <span class="nav-lr-main">
          <b>Version <span class="about-version">{__APP_VERSION__}</span></b>
          <small class="about-meta">
            Build <span class="about-sha">{build.sha}</span> · {build.dirty ? 'uncommitted changes - not reproducible · ' : ''}built {new Date(build.time).toLocaleString()}
          </small>
        </span>
        <button
          type="button"
          class="nv-b nv-b-sm about-details-btn"
          aria-expanded={detailsOpen()}
          onClick={() => { const v = !detailsOpen(); setDetailsOpen(v); writeFlag(v); if (v) reportFrame(); }}
        >
          {detailsOpen() ? 'Hide details' : 'Details'}
        </button>
      </div>
      </div>
      <Show when={frameInfo()}>
        {(f) => (
          <div class="about-lines">
            <Show when={detailsOpen()}>
              <span class="about-line">
                Screen {f().vw}&times;{f().vh} @{f().dpr}x &middot; usable {f().availW}&times;{f().availH} &middot;{' '}
                drawn for {f().designW}&times;{f().designH} &middot; {f().scalePct}%
              </span>
              <Show when={frame()}>
                {(g) => (
                  <span class="about-line" data-frame-report>
                    Canvas {g().canvas.w}&times;{g().canvas.h} &middot; strips {g().shellStrips.top} top / {g().shellStrips.bottom} bottom
                    {' '}(iOS {g().iosInsets.top} / {g().iosInsets.bottom}) &middot; bar {g().bar.h}
                    {g().lowest ? ` · lowest control ${g().lowest!.hiddenBelowCanvas > 0 || g().lowest!.hiddenBelowScreen > 0 ? `${Math.max(g().lowest!.hiddenBelowCanvas, g().lowest!.hiddenBelowScreen)} hidden` : 'fully visible'}` : ''}
                    {frameSent() ? ` · ${frameSent()}` : ''}
                  </span>
                )}
              </Show>
              <Show when={bridges().length > 0}>
                <span class="about-line">
                  Bridge scope bundle at pairing: {bridges().map((b) => `${b.name}: v${b.scope_bundle_version ?? '?'}`).join(' · ')}
                  {scopeMixed() ? ' (they disagree)' : ''}
                </span>
              </Show>
            </Show>
            <Show when={f().unitH > 0 && f().unitH !== f().layoutH}>
              <span class="about-line">
                Height: layout {f().layoutH} &middot; 100vh {f().unitH} &middot; using {Math.max(f().layoutH, f().unitH)}
                {f().standalone ? ' (installed)' : ' (browser)'}
              </span>
            </Show>
            <Show when={f().bandTop > 0 || f().bandBottom > 0 || f().bandSide > 0}>
              <span class="about-line">
                Letterbox: {f().bandTop} top &middot; {f().bandBottom} bottom &middot; {f().bandSide} each side
              </span>
            </Show>
          </div>
        )}
      </Show>
    </div>
  );
}
